import React, { useEffect, useState, useRef } from "react";
import secureAxios from "../../utils/secureAxios";
import WidgetContainer from "./WidgetContainer";
import { getUserWidgetPrefs, getAvailableWidgets } from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";
import CustomizeDashboardWidgets from "./CustomizeDashboardWidgets";
import { createBackgroundRequestConfig } from "../../hooks/useAuthIntegration";

const Dashboard = () => {
  const { user } = useAuth();
  const [userWidgets, setUserWidgets] = useState([]);
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminDataViewMode, setAdminDataViewMode] = useState("all");
  const [hasVisibilityLinks, setHasVisibilityLinks] = useState(false);

  // Prevent duplicate API calls with ref
  const hasFetchedRef = useRef(false);
  const currentUserIdRef = useRef(null);

  const isAdmin = user?.is_admin || false;
  const onlyMyData = !isAdmin || adminDataViewMode === "personal";

  // Single consolidated data fetch function
  useEffect(() => {
    const loadDashboardData = async () => {
      // Only proceed if we have a user and haven't fetched for this user yet
      if (!user?.user_id) {
        setLoading(false);
        return;
      }

      // If user changed, reset the fetch flag
      if (currentUserIdRef.current !== user.user_id) {
        hasFetchedRef.current = false;
        currentUserIdRef.current = user.user_id;
      }

      // Prevent duplicate calls for the same user
      if (hasFetchedRef.current) {
        return;
      }

      hasFetchedRef.current = true;
      setLoading(true);

      try {
        // Consolidate all API calls into a single Promise.all
        const [prefsRes, availableRes, visibilityRes] = await Promise.all([
          secureAxios.get(
            "/api/dashboard-widgets/preferences",
            createBackgroundRequestConfig()
          ),
          secureAxios.get(
            "/api/dashboard-widgets/available",
            createBackgroundRequestConfig()
          ),
          secureAxios
            .get(
              "/api/users/visibility-links/viewer",
              createBackgroundRequestConfig()
            )
            .catch((err) => {
              if (err.response?.status === 404) {
                return { data: { hasVisibilityLinks: false } };
              }
              if (process.env.NODE_ENV === "development") {
                console.error("Error checking viewer visibility:", err);
              }
              return { data: { hasVisibilityLinks: false } };
            }),
        ]);

        // Update all state at once to prevent multiple re-renders
        setUserWidgets(Array.isArray(prefsRes.data) ? prefsRes.data : []);
        setAvailableWidgets(
          Array.isArray(availableRes.data) ? availableRes.data : []
        );
        setHasVisibilityLinks(visibilityRes.data.hasVisibilityLinks === true);
      } catch (err) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error loading dashboard data:", err);
        }
        // Set empty arrays on error to prevent UI issues
        setUserWidgets([]);
        setAvailableWidgets([]);
        setHasVisibilityLinks(false);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user?.user_id]); // Single dependency

  // Refresh function for after saving preferences
  const refreshDashboardData = async () => {
    if (!user?.user_id) return;

    // Reset fetch flag to allow refresh
    hasFetchedRef.current = false;

    try {
      setLoading(true);

      const [prefsRes, availableRes] = await Promise.all([
        getUserWidgetPrefs(),
        getAvailableWidgets(),
      ]);

      setUserWidgets(Array.isArray(prefsRes.data) ? prefsRes.data : []);
      setAvailableWidgets(
        Array.isArray(availableRes.data) ? availableRes.data : []
      );

      // Mark as fetched again
      hasFetchedRef.current = true;
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error refreshing dashboard data:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Compute visible widgets
  const visibleWidgets = userWidgets
    .filter((w) => w.is_visible)
    .map((w) => {
      const cleanId = w.widget_type?.trim();
      const def = availableWidgets.find((a) => a.id?.trim() === cleanId);
      if (!def) return null;
      if (def.adminOnly && !isAdmin) return null;
      return {
        ...w,
        widget_type: cleanId,
        label: def.label?.trim() || "Unknown Widget",
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div className="container mt-4 px-3 px-md-4 px-lg-5">
      <h2>📊 Dashboard</h2>

      {/* Admin data view toggle */}
      {isAdmin && (
        <div className="mb-3">
          <div className="btn-group" role="group" aria-label="Data view toggle">
            <button
              type="button"
              className={`btn ${
                adminDataViewMode === "all"
                  ? "btn-primary"
                  : "btn-outline-primary"
              }`}
              onClick={() => setAdminDataViewMode("all")}
            >
              <i className="bi bi-people-fill me-1"></i>
              All Employees
            </button>
            <button
              type="button"
              className={`btn ${
                adminDataViewMode === "personal"
                  ? "btn-primary"
                  : "btn-outline-primary"
              }`}
              onClick={() => setAdminDataViewMode("personal")}
            >
              <i className="bi bi-person-fill me-1"></i>
              My Data Only
            </button>
          </div>
          <small className="text-muted ms-2">
            Viewing:{" "}
            {adminDataViewMode === "all"
              ? "All employee data"
              : "Your personal data only"}
          </small>
        </div>
      )}

      {/* Visibility links info for non-admins */}
      {hasVisibilityLinks && !isAdmin && (
        <div className="alert alert-info mb-3">
          <i className="bi bi-info-circle me-2"></i>
          You are viewing your personal data only.
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          Loading widgets...
        </div>
      ) : visibleWidgets.length === 0 && !showSettings ? (
        /* No widgets state */
        <div className="alert alert-warning">
          <p className="mb-2">No widgets are currently visible.</p>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowSettings(true)}
          >
            ⚙️ Configure Widgets
          </button>
        </div>
      ) : (
        /* Main dashboard content */
        <>
          <button
            className="btn btn-outline-primary mb-3"
            onClick={() => setShowSettings(true)}
          >
            ⚙️ Customize Widgets
          </button>

          <WidgetContainer widgets={visibleWidgets} onlyMyData={onlyMyData} />
        </>
      )}

      {/* Widget customization modal */}
      {showSettings && !loading && (
        <CustomizeDashboardWidgets
          availableWidgets={availableWidgets}
          currentPrefs={userWidgets}
          onClose={() => setShowSettings(false)}
          onSaved={refreshDashboardData}
        />
      )}
    </div>
  );
};

export default Dashboard;
